import { useCallback, useEffect, useState } from 'react'
import { ROLE_DESCRIPTIONS, ROLE_LABELS, ROLES, type Role } from '../../shared/roles'
import type { AppUser, AuditEntry, TwoFactorMethod, UserLink } from '../../shared/types'
import { api } from '../api'
import { Card, ErrorNote, Pill, Spinner } from '../components'
import { dateTimeLabel } from '../format'
import { useSession } from '../session-context'

/**
 * Accounts and roles. Admin only — the tab is not drawn for anyone else and
 * the server refuses the calls regardless.
 *
 * Creating an account sends an invitation link; the user chooses their own
 * password on it, so no admin ever knows it. When the service cannot send
 * email the link is shown here once instead, to pass on by hand.
 */
const TWO_FACTOR_LABELS: Record<TwoFactorMethod, string> = {
  none: 'password only',
  email: 'code by email',
  totp: 'authenticator app',
}

export function UsersScreen() {
  const { session } = useSession()
  const [users, setUsers] = useState<AppUser[] | null>(null)
  const [mailEnabled, setMailEnabled] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState<number | 'create' | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [issued, setIssued] = useState<UserLink | null>(null)

  const load = useCallback(async () => {
    const r = await api.users()
    setUsers(r.users)
    setMailEnabled(r.mailEnabled)
  }, [])
  useEffect(() => {
    load().catch((e: unknown) => setError(e))
  }, [load])

  const act = async (key: number | 'create', fn: () => Promise<unknown>): Promise<void> => {
    setBusy(key)
    setMessage(null)
    try {
      await fn()
      await load()
    } catch (e) {
      setMessage(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  if (error !== null) return <ErrorNote error={error} />
  if (users === null) return <Spinner />

  return (
    <>
      {session?.sharedPasswordEnabled === true && (
        <div className="banner warn">
          <strong>The shared password still works</strong> and signs in as admin. Once everyone
          who needs access has an account below, set <code>ALLOW_SHARED_PASSWORD=false</code>{' '}
          on the service so it stops.
        </div>
      )}
      {!mailEnabled ? (
        <div className="banner warn">
          <strong>This service cannot send email</strong>, so invitation and reset links are
          shown here for you to pass on, and "code by email" sign-in falls back to password
          only. Set <code>MAIL_DRIVER</code> (brevo) with <code>MAIL_FROM</code> and
          the provider's key to change that.
        </div>
      ) : (
        <p className="note">
          Email is on.{' '}
          <button
            type="button"
            className="link-button"
            disabled={busy === 'create'}
            onClick={() =>
              void act('create', async () => {
                const r = await api.mailTest()
                setMessage(r.sent ? `Test email sent to ${r.to}.` : `Test email failed: ${r.reason ?? 'unknown'}`)
              })
            }
          >
            Send me a test email
          </button>
        </p>
      )}
      {message !== null && <div className="banner warn">{message}</div>}
      {issued !== null && (
        <div className="banner ok">
          <strong>
            {issued.kind === 'invite' ? 'Invitation' : 'Reset link'} for {issued.user.username}
            {issued.emailed ? ' emailed.' : ' — not emailed.'}
          </strong>
          {!issued.emailed && issued.reason !== null && <div className="desc">{issued.reason}</div>}
          <div className="desc">
            The link, in case you need to pass it on. It works once and expires{' '}
            {dateTimeLabel(issued.expiresAt)}.
            <br />
            <code style={{ fontSize: 12, userSelect: 'all', wordBreak: 'break-all' }}>
              {issued.link.startsWith('/') ? `${window.location.origin}${issued.link}` : issued.link}
            </code>
            <br />
            <button type="button" className="link-button" onClick={() => setIssued(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}

      <Card title="Roles" subtitle="Each role can do everything the ones before it can">
        <table>
          <tbody>
            {ROLES.map((r) => (
              <tr key={r}>
                <td style={{ width: 140 }}>
                  <strong>{ROLE_LABELS[r]}</strong>
                </td>
                <td className="muted">{ROLE_DESCRIPTIONS[r]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <CreateUser
        busy={busy === 'create'}
        onCreate={(username, role, displayName) =>
          void act('create', async () => setIssued(await api.createUser(username, role, displayName)))
        }
      />

      <Card title="Accounts" flush>
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Role</th>
              <th>After the password</th>
              <th>Status</th>
              <th>Last signed in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="muted">
                  No accounts yet — everyone is using the shared password.
                </td>
              </tr>
            )}
            {users.map((u) => {
              const self = u.id === session?.userId
              const rowBusy = busy === u.id
              return (
                <tr key={u.id} className={u.disabled ? 'dulled' : undefined}>
                  <td>
                    <button
                      type="button"
                      className="link-button"
                      disabled={rowBusy}
                      title="Change the email address"
                      onClick={() => {
                        const v = window.prompt(`Email address for this account:`, u.username)
                        if (v !== null && v.trim() !== '' && v.trim().toLowerCase() !== u.username) {
                          void act(u.id, () => api.updateUser(u.id, { username: v.trim() }))
                        }
                      }}
                    >
                      <code>{u.username}</code>
                    </button>
                    {self && <span className="muted"> (you)</span>}
                    <div className="desc">
                      <button
                        type="button"
                        className="link-button"
                        disabled={rowBusy}
                        title="Change the name"
                        onClick={() => {
                          const v = window.prompt(`Name for ${u.username}:`, u.displayName ?? '')
                          if (v !== null) void act(u.id, () => api.updateUser(u.id, { displayName: v.trim() === '' ? null : v.trim() }))
                        }}
                      >
                        {u.displayName ?? 'add a name…'}
                      </button>
                    </div>
                  </td>
                  <td>
                    <select
                      value={u.role}
                      disabled={rowBusy || self}
                      title={self ? 'You cannot change your own role' : undefined}
                      onChange={(e) =>
                        void act(u.id, () => api.updateUser(u.id, { role: e.target.value as Role }))
                      }
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      value={u.twoFactor}
                      disabled={rowBusy}
                      onChange={(e) =>
                        void act(u.id, () => api.updateUser(u.id, { twoFactor: e.target.value as TwoFactorMethod }))
                      }
                    >
                      {(Object.keys(TWO_FACTOR_LABELS) as TwoFactorMethod[]).map((m) => (
                        <option key={m} value={m}>
                          {TWO_FACTOR_LABELS[m]}
                        </option>
                      ))}
                    </select>
                    {u.twoFactor === 'totp' && (
                      <div className="desc">
                        {u.totpEnrolled ? 'enrolled' : 'not yet set up — asked at next sign-in'}
                        {u.totpEnrolled && (
                          <>
                            {' · '}
                            <button
                              type="button"
                              className="link-button"
                              disabled={rowBusy}
                              title="Lost phone: drop the authenticator and its recovery codes"
                              onClick={() => {
                                if (window.confirm(`Reset the authenticator for ${u.username}? They will set one up again at next sign-in.`)) {
                                  void act(u.id, () => api.resetUserTotp(u.id))
                                }
                              }}
                            >
                              reset
                            </button>
                          </>
                        )}
                      </div>
                    )}
                    {u.twoFactor === 'email' && !mailEnabled && (
                      <div className="desc">not possible — email not configured</div>
                    )}
                  </td>
                  <td>
                    {u.disabled ? (
                      <Pill tone="neutral">disabled</Pill>
                    ) : u.invitedAt !== null ? (
                      <Pill tone="warn">invited</Pill>
                    ) : u.mustChangePassword ? (
                      <Pill tone="warn">must change password</Pill>
                    ) : (
                      <Pill tone="ok">active</Pill>
                    )}
                  </td>
                  <td className="muted">
                    {u.lastLoginAt === null ? 'never' : dateTimeLabel(u.lastLoginAt)}
                    <div className="desc">
                      created {dateTimeLabel(u.createdAt)}
                      {u.createdBy !== null && ` by ${u.createdBy}`}
                    </div>
                  </td>
                  <td>
                    {u.invitedAt !== null ? (
                      <button
                        type="button"
                        className="link-button"
                        disabled={rowBusy || u.disabled}
                        onClick={() => void act(u.id, async () => setIssued(await api.resendInvite(u.id)))}
                      >
                        Send invitation again
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="link-button"
                        disabled={rowBusy || u.disabled}
                        onClick={() => void act(u.id, async () => setIssued(await api.resetUserPassword(u.id)))}
                      >
                        Send reset link
                      </button>
                    )}
                    {' · '}
                    <button
                      type="button"
                      className="link-button"
                      disabled={rowBusy || self}
                      title={self ? 'You cannot disable yourself' : undefined}
                      onClick={() =>
                        void act(u.id, () => api.updateUser(u.id, { disabled: !u.disabled }))
                      }
                    >
                      {u.disabled ? 'Enable' : 'Disable'}
                    </button>
                    {' · '}
                    <button
                      type="button"
                      className="link-button"
                      disabled={rowBusy || self}
                      title={self ? 'You cannot delete yourself' : 'Remove the account; the audit trail keeps the name'}
                      onClick={() => {
                        if (window.confirm(`Delete ${u.username}? They are signed out at once and the account is gone. What they did stays in the audit trail.`)) {
                          void act(u.id, () => api.deleteUser(u.id))
                        }
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>

      <AuditCard />
    </>
  )
}

const ACTION_LABELS: Record<string, string> = {
  'assumption.update': 'changed an assumption',
  'site.developer.set': 'set the developer on a site',
  'site.developer.add': 'added a developer to a site',
  'site.developer.update': 'changed a developer on a site',
  'site.developer.remove': 'removed a developer from a site',
  'upload.receive': 'uploaded a file',
  'upload.load': 'loaded an uploaded file',
  'upload.discard': 'discarded an upload',
  'upload.reload': 'reloaded an earlier file',
  'pickup.run': 'ran the SharePoint pickup',
  'feed.owner.update': 'changed a feed owner',
  'digest.send': 'emailed the weekly digest',
  'user.create': 'created an account',
  'user.invite': 'resent an invitation',
  'user.reset_link': 'sent a reset link',
  'user.totp.reset': "reset an account's authenticator",
  'user.update': 'changed an account',
  'user.rename': 'changed an account’s email address',
  'user.delete': 'deleted an account',
  'mail.test': 'sent a test email',
  'account.password.change': 'changed their own password',
  'account.totp.enrol': 'enrolled an authenticator',
  'account.totp.disable': 'turned their authenticator off',
  'account.totp.recovery_codes': 'issued new recovery codes',
  'account.totp.setup': 'started authenticator set-up',
  'account.logout': 'signed out',
}

/** Who did what, newest first. Reads only; nothing here can be edited. */
function AuditCard() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null)
  const [nextBefore, setNextBefore] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (before: number | null) => {
    const r = await api.audit(before)
    setEntries((prev) => (before === null || prev === null ? r.entries : [...prev, ...r.entries]))
    setNextBefore(r.nextBefore)
  }, [])
  useEffect(() => {
    load(null).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [load])

  return (
    <Card title="Who did what" subtitle="Every change made through the tool, newest first. Ingest runs are attributed on the Data screen." flush>
      {error !== null && <div className="body"><div className="note">{error}</div></div>}
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Who</th>
            <th>Did</th>
            <th>To</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {entries === null && (
            <tr><td colSpan={5} className="muted">Loading…</td></tr>
          )}
          {entries !== null && entries.length === 0 && (
            <tr><td colSpan={5} className="muted">Nothing recorded yet.</td></tr>
          )}
          {(entries ?? []).map((e) => (
            <tr key={e.id}>
              <td className="muted">{dateTimeLabel(e.at)}</td>
              <td>
                <code>{e.username}</code>
              </td>
              <td>{ACTION_LABELS[e.action] ?? e.action}</td>
              <td className="muted">{e.target ?? '—'}</td>
              <td className="muted">
                {Object.keys(e.detail).length === 0 ? '' : Object.entries(e.detail).map(([k, v]) => `${k}: ${String(v)}`).join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {nextBefore !== null && (
        <div className="body">
          <button type="button" className="link-button" onClick={() => void load(nextBefore).catch((e: unknown) => setError(String(e)))}>
            Earlier
          </button>
        </div>
      )}
    </Card>
  )
}

function CreateUser({
  busy,
  onCreate,
}: {
  busy: boolean
  onCreate: (username: string, role: Role, displayName: string) => void
}) {
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<Role>('read_only')
  return (
    <Card
      title="Add an account"
      subtitle="Their work email address is the username; the invitation goes there"
    >
      <form
        className="dev-add"
        onSubmit={(e) => {
          e.preventDefault()
          if (username.trim() === '') return
          onCreate(username.trim(), role, displayName.trim())
          setUsername('')
          setDisplayName('')
        }}
      >
        <input
          type="email"
          placeholder="work email address"
          value={username}
          disabled={busy}
          autoComplete="off"
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="text"
          placeholder="name (optional)"
          value={displayName}
          disabled={busy}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <select value={role} disabled={busy} onChange={(e) => setRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        <button type="submit" disabled={busy || username.trim() === ''}>
          {busy ? 'Sending…' : 'Create and invite'}
        </button>
      </form>
    </Card>
  )
}
